import { openOauthWindow } from '@common/oAuth/utils';
import { Private_Routes } from '@common/url';
import { normalizeStringForTestId } from '@common/utils';
import { LogoAzureADIcon, LogoGoogleIcon, LogoOktaIcon, LogoProphecyWithNameIcon, LogoSamlIcon } from '@prophecy/icons';
import { ObjectLiteral } from '@prophecy/interfaces/generic';
import { resolveCssVariable, theme } from '@prophecy/theme';
import { Button, Separator, Spinner, Stack, Text, Tooltip } from '@prophecy/ui-v3';
import { message } from '@prophecy/ui-v3/ui-compat';
import { FROZEN_EMPTY_ARRAY } from '@prophecy/utils/constants';
import { BASE_PATH, navigateAndRefresh } from '@prophecy/utils/history';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { parseJSON } from '@prophecy/utils/string';
import { appendQueryParams, isSameHost } from '@prophecy/utils/url';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { useUnsecuredGlobalVariables } from '../../context/globalConfig/unsecuredConfigHook';
import {
  getAllIdpTypes,
  oAuthAzureADAuthenticate,
  oAuthGoogleAuthenticate,
  oAuthSamlAuthenticate
} from '../../data/apis/api';
import { AuthTypes, ProviderType } from '../../data/apis/types';
import { GlobalWindow, MetaPixelFbq } from '../../utils/types';
import { IdpProviderLabelMap } from '../UserSettings/config/admin';
import { META_PIXEL_ID, UTM_COOKIE_NAME, UTM_SESSION_KEY, allowedDomain, allowedMetaPixelDomains } from './constant';
import { initializeZoomInfo } from './signup/zoominfo';

export const StyledSeparator = styled(Separator)`
  position: relative;
`;

export const Header = styled(Stack).attrs({
  direction: 'horizontal',
  gap: theme.spaces.x16,
  alignY: 'center',
  align: 'space-between'
})`
  padding: ${theme.spaces.x20} ${theme.spaces.x32};
  position: absolute;
  width: 100%;
  z-index: ${theme.zLayer.s};
  @media (max-width: ${resolveCssVariable(theme.breakpoints.s)}) {
    padding: ${theme.spaces.x16};
    position: unset;
  }
`;

export const StyledForm = styled.form`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-width: 0;
`;

const StretchedButton = styled(Button)`
  flex-grow: 1;
  width: 100%;
` as unknown as typeof Button;
export const StyledLinkButton = styled(Button)<{ $isProfEditionEnabled?: boolean }>`
  padding: 0;
  height: auto;
  ${({ $isProfEditionEnabled }) =>
    $isProfEditionEnabled &&
    css`
      @media (min-width: ${resolveCssVariable(theme.breakpoints.s)}) {
        position: absolute;
        top: ${theme.spaces.x32};
        left: ${theme.spaces.x32};
      }
    `}
`;

export const StyledFormContainer = styled.div`
  width: 450px;
  max-width: 100%;
  @media (max-width: ${resolveCssVariable(theme.breakpoints.s)}) {
    height: 100%;
  }
`;

export const StyledProphecyLogo = styled(LogoProphecyWithNameIcon)`
  position: absolute;
  margin: ${theme.spaces.x32};
`;

export function PageLink({ label, buttonText, url }: { label: string; buttonText: string; url: string }) {
  return (
    <Stack direction='horizontal' gap={theme.spaces.x4} alignY='center'>
      <Text level='sm' tone={theme.colors.gray500}>
        {label}
      </Text>
      <StyledLinkButton
        elementType='link'
        variant='link'
        to={url}
        data-test-id={`auth-common-page-link-${normalizeStringForTestId(buttonText)}-button`}>
        <Text level='sm'>{buttonText}</Text>
      </StyledLinkButton>
    </Stack>
  );
}

export const useQualifiedChatBox = () => {
  const unsecureGlobalConfigManager = useUnsecuredGlobalVariables();
  const isCrispChatEnabled = unsecureGlobalConfigManager.isCrispChatEnabled;
  useEffect(() => {
    if (!isCrispChatEnabled) {
      return;
    }

    (function (w, q) {
      w['QualifiedObject'] = q;
      w[q] =
        w[q] ||
        function () {
          (w[q].q = w[q].q || []).push(arguments);
        };
    })(window as ObjectLiteral, 'qualified');

    let script = document.createElement('script');
    script.src = 'https://js.qualified.com/qualified.js?token=K15YXsxGRU2PqcHL';
    script.async = true;
    document.getElementsByTagName('head')[0].appendChild(script);
    return () => {
      script?.remove();
    };
  }, [isCrispChatEnabled]);
};

export const ExternalAuthButtons = ({
  embedded,
  isLogin,
  showGoogleSignUpText,
  redirectUrl
}: {
  embedded?: boolean;
  isLogin?: boolean;
  showGoogleSignUpText?: boolean;
  redirectUrl?: string;
}) => {
  const { idpTypesSupported, isLoading, signInWithAzureAD, signInWithSaml, signInWithGoogle } = useExternalLogin(
    embedded,
    redirectUrl
  );
  if (isLoading) {
    return <Spinner />;
  }

  // for saml type, we show icon based on the "idp" property
  const getButtonTextAndLogoForSaml = (idpType: IDPSupportedType) => {
    const providerType =
      idpType.idp === ProviderType.others ? AuthTypes.saml.toUpperCase() : idpType.idp || ProviderType.okta;
    let tooltipText = `Sign in with ${providerType}`;
    let icon = <LogoOktaIcon aria-hidden='true' />;
    if (idpType.idp === ProviderType.google) {
      icon = <LogoGoogleIcon aria-hidden='true' />;
    } else if (idpType.idp === ProviderType.others) {
      icon = <LogoSamlIcon aria-hidden='true' />;
    } else if (idpType.idp === ProviderType.azuread) {
      tooltipText = `Sign in with ${IdpProviderLabelMap[idpType.idp]}`;
      icon = <LogoAzureADIcon aria-hidden='true' />;
    }
    return { tooltipText, icon };
  };

  return idpTypesSupported.length > 0 ? (
    <Stack
      gap={theme.spaces.x16}
      padding={isLogin ? '0' : `${theme.spaces.x24} 0 0`}
      direction='horizontal'
      align='center'>
      {idpTypesSupported.map((idpType) => {
        if (idpType.type === AuthTypes.azuread) {
          return (
            <Tooltip key={idpType.type} title='Sign in with Azure'>
              <Stack width='100%'>
                <StretchedButton
                  iconPlacement='left'
                  icon={<LogoAzureADIcon aria-hidden='true' />}
                  variant='secondaryGray'
                  onClick={() => signInWithAzureAD()}
                  aria-label='Sign in with Azure'
                  data-test-id='auth-common-azure-ad-button'
                />
              </Stack>
            </Tooltip>
          );
        } else if (idpType.type === AuthTypes.google) {
          const tooltipText = showGoogleSignUpText ? `Sign up with Google` : `Sign in with Google`;
          return (
            <Tooltip key={idpType.type} title={tooltipText}>
              <Stack width='100%'>
                <StretchedButton
                  iconPlacement='left'
                  icon={<LogoGoogleIcon aria-hidden='true' />}
                  variant='secondaryGray'
                  onClick={() => signInWithGoogle()}
                  aria-label={tooltipText}
                  data-test-id='auth-common-google-button'>
                  {showGoogleSignUpText ? tooltipText : undefined}
                </StretchedButton>
              </Stack>
            </Tooltip>
          );
        } else if (idpType.type === AuthTypes.saml) {
          const { tooltipText, icon } = getButtonTextAndLogoForSaml(idpType);
          return (
            <Tooltip key={idpType.type} title={tooltipText}>
              <Stack width='100%'>
                <StretchedButton
                  iconPlacement='left'
                  icon={icon}
                  variant='secondaryGray'
                  onClick={() => signInWithSaml()}
                  aria-label={tooltipText}
                  data-test-id='auth-common-saml-button'
                />
              </Stack>
            </Tooltip>
          );
        } else {
          return null;
        }
      })}
    </Stack>
  ) : null;
};

export type IDPSupportedType = { idp?: ProviderType; type: AuthTypes };

export function useExternalLogin(embedded?: boolean, redirectUrl?: string) {
  const unsecureGlobalConfigManager = useUnsecuredGlobalVariables();
  const { data, isLoading } = useQuery(['idpconfigtypes'], getAllIdpTypes, {
    enabled: unsecureGlobalConfigManager.isConfigurableAuthEnabled
  });
  let idpTypesSupported: IDPSupportedType[] = data?.data?.idpTypes || FROZEN_EMPTY_ARRAY;

  const redirectToOauth = (url: string) => {
    openOauthWindow(url, () => {
      if (embedded) {
        redirectParentToUrl('/');
      } else {
        if (redirectUrl) {
          window.location.assign(redirectUrl);
        } else {
          window.location.reload();
        }
      }
    });
  };

  async function signInWithAzureAD(params: { identifier?: string } = {}) {
    //linkedin track
    (window as unknown as GlobalWindow).lintrk?.('track', { conversion_id: 8934260 });
    try {
      const oAuthResp = await oAuthAzureADAuthenticate(params);
      redirectToOauth(oAuthResp.action);
    } catch (e) {
      message.error({ content: 'Some error occurred.' });
    }
  }

  async function signInWithGoogle(params: { identifier?: string } = {}) {
    //linkedin track
    (window as unknown as GlobalWindow).lintrk?.('track', { conversion_id: 8934260 });
    try {
      const oAuthResp = await oAuthGoogleAuthenticate(params);
      redirectToOauth(oAuthResp.action);
    } catch (e) {
      message.error({ content: 'Some error occurred.' });
    }
  }

  async function signInWithSaml(params: { identifier?: string } = {}) {
    try {
      const oAuthResp = await oAuthSamlAuthenticate(params);

      const url = appendQueryParams(oAuthResp.action, {
        SAMLRequest: oAuthResp.SAMLRequest,
        RelayState: oAuthResp.RelayState
      });

      redirectToOauth(url);
    } catch (e) {
      message.error({ content: 'Some error occurred.' });
    }
  }

  return { idpTypesSupported, isLoading, signInWithAzureAD, signInWithSaml, signInWithGoogle };
}

function getUtmParamsFromCookie(): Record<string, string> | null {
  const match = document.cookie.split('; ').find((row) => row.startsWith(`${UTM_COOKIE_NAME}=`));
  if (!match) return null;
  const encoded = match.slice(UTM_COOKIE_NAME.length + 1);
  return parseJSON(decodeURIComponent(encoded), {}) as Record<string, string>;
}

export function getStoredUtmParams(): Record<string, string> | null {
  const raw = sessionStorage.getItem(UTM_SESSION_KEY);
  if (raw) return parseJSON(raw, {}) as Record<string, string>;
  return getUtmParamsFromCookie();
}

export function useHubSpot() {
  useEffect(() => {
    if (!allowedDomain.includes(window.location.host)) return;
    const script = document.createElement('script');
    script.src = 'https://js.hs-scripts.com/22397615.js';
    script.async = true;
    document.getElementsByTagName('head')[0].appendChild(script);
    return () => {
      script.remove();
    };
  }, []);
}

export function useMetaPixel() {
  useEffect(() => {
    if (!allowedMetaPixelDomains.includes(window.location.host)) return;
    const globalWindow = window as unknown as GlobalWindow;

    // Prevent injecting multiple times
    if (globalWindow.fbq) return;
    const fbq = function (...args: unknown[]) {
      if (fbq.callMethod) {
        fbq.callMethod.apply(fbq, args);
      } else {
        fbq.queue.push(args);
      }
    } as MetaPixelFbq;
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
    globalWindow.fbq = fbq;
    if (!globalWindow._fbq) globalWindow._fbq = fbq;

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.getElementsByTagName('head')[0].appendChild(script);
    // Initialize and track initial page view
    fbq('init', META_PIXEL_ID);
    fbq('track', 'PageView');
    return () => {
      script.remove();
      delete globalWindow.fbq;
      delete globalWindow._fbq;
    };
  }, []);
}

export function useLinkedInTrack() {
  useEffect(() => {
    if (!allowedDomain.includes(window.location.host)) return;
    const globalWindow = window as unknown as GlobalWindow;
    const _linkedin_partner_id = '2212284';
    globalWindow._linkedin_data_partner_ids = globalWindow._linkedin_data_partner_ids || [];
    globalWindow._linkedin_data_partner_ids.push(_linkedin_partner_id);

    if (!globalWindow.lintrk) {
      globalWindow.lintrk = function (a, b) {
        globalWindow.lintrk?.q?.push([a, b]);
      };
      globalWindow.lintrk.q = [];
    }

    const script = document.createElement('script');
    script.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
    script.async = true;
    document.getElementsByTagName('head')[0].appendChild(script);
    return () => {
      script.remove();
    };
  }, []);
}

export function useZoomInfo() {
  useEffect(() => {
    if (!allowedDomain.includes(window.location.host)) return;

    const script = initializeZoomInfo();
    return () => {
      script.remove();
    };
  }, []);
}

export const useSignupParamsFromURL = () => {
  const [searchParams] = useSearchParams();
  const strTeamNameUpdationAllowed = searchParams.get('teamNameUpdationAllowed') || '';

  const teamName = searchParams.get('teamName') || '';
  const company = searchParams.get('company') || '';
  const nameInputNeeded = searchParams.get('nameInputNeeded') === 'true';
  const firstName = searchParams.get('firstName') || '';
  const lastName = searchParams.get('lastName') || '';
  const email = searchParams.get('email') || '';
  const token = searchParams.get('token') || '';
  const teamUID = searchParams.get('teamUID') || '';
  const otp = searchParams.get('otp') || '';
  const verifiedUser = searchParams.get('verifiedUser') || '';
  const invitation = searchParams.get('invitation') || '';
  const teamid = searchParams.get('teamid') || '';
  const uid = searchParams.get('uid') || '';
  const stepParam = searchParams.get('step');
  const teamNameUpdationAllowed = strTeamNameUpdationAllowed === 'true';
  const redirect = searchParams.get('redirect') || undefined;
  return {
    redirect,
    otp,
    uid,
    verifiedUser,
    teamid,
    stepParam,
    firstName,
    lastName,
    teamName,
    invitation,
    company,
    nameInputNeeded,
    email,
    token,
    teamUID,
    teamNameUpdationAllowed,
    strTeamNameUpdationAllowed
  };
};

export function redirectParentToUrl(url: string) {
  const baseUrl = `${window.location.origin}${BASE_PATH}`;

  if (!url.startsWith(baseUrl)) url = `${baseUrl}${url}`;

  window.top?.postMessage({ type: 'ProphecyAppSignup', url }, '*');
}

export function useRedirectAfterAuth() {
  const [searchParams] = useSearchParams();
  const redirectParam = searchParams.get('redirect') || undefined;

  return usePersistentCallback(() => {
    if (redirectParam && isSameHost(redirectParam, window.location.host)) {
      window.location.href = redirectParam;
    } else {
      navigateAndRefresh(Private_Routes.Home.getUrl());
    }
  });
}
